// @core/stores/booking/BookingBookingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { bookingApi } from '@core/api';
import type {
  Booking,
  CreateBookingRequest,
  ListBookingsResponse,
} from '@core/types/booking';
import type { PageInfo } from '@core/types/pagination';

class BookingBookingStore {
  // List state
  items: Booking[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Booking | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Booking>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Booking | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Booking | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create booking
   */
  async CreateBooking(data: CreateBookingRequest): Promise<Booking> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await bookingApi.CreateBooking(data);
      const item = response.booking;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get booking
   */
  async GetBooking(booking_id: string): Promise<Booking> {
    // Check cache first
    const cached = this.cache.get(booking_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await bookingApi.GetBooking(booking_id);
      const item = response.booking;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List bookings
   */
  async ListBookings(params?: { booking_type_id?: string | number | boolean; cursor?: string | number | boolean; date_from?: string | number | boolean; date_to?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; resource_id?: string | number | boolean; sort?: string | number | boolean; status?: string | number | boolean; }): Promise<ListBookingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bookingApi.ListBookings(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my bookings
   */
  async ListMyBookings(params?: { cursor?: string | number | boolean; date_from?: string | number | boolean; date_to?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; sort?: string | number | boolean; status?: string | number | boolean; }): Promise<ListBookingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bookingApi.ListMyBookings(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List provider bookings
   */
  async ListProviderBookings(params?: { cursor?: string | number | boolean; date_from?: string | number | boolean; date_to?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; resource_id?: string | number | boolean; sort?: string | number | boolean; status?: string | number | boolean; }): Promise<ListBookingsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await bookingApi.ListProviderBookings(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const bookingBookingStore = new BookingBookingStore();
